import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Search, Filter, Tag, ArrowUpDown, CheckCircle2, Phone, MessageCircle } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import type { ServiceCategory, ServiceItem } from "@/lib/services";
import { COMPANY } from "@/lib/company";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildOrganizationSchema,
  buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";

const BUDGET_TIERS: { id: string; label: string; min: number; max: number }[] = [
  { id: "all", label: "ทุกงบ", min: 0, max: Infinity },
  { id: "lt5k", label: "< 5,000 ฿", min: 0, max: 5000 },
  { id: "5to20k", label: "5–20K ฿", min: 5000, max: 20000 },
  { id: "20to50k", label: "20–50K ฿", min: 20000, max: 50000 },
  { id: "50to100k", label: "50K–1 แสน", min: 50000, max: 100000 },
  { id: "gt100k", label: "> 1 แสน", min: 100000, max: Infinity },
];

type SortKey = "category" | "low" | "high" | "alpha";

const FAQS = [
  { q: "ราคาในตารางรวมค่าธรรมเนียมราชการแล้วหรือยัง?", a: "ราคาที่แสดงเป็นค่าบริการเริ่มต้น ค่าธรรมเนียมราชการ (กงสุล สถานทูต กระทรวง) คิดตามใบเสร็จจริง ทีมงานจะแจ้งยอดสุทธิก่อนเริ่มงานทุกครั้ง" },
  { q: "ทำไมราคาเป็นช่วง 'เริ่มต้น – สูงสุด'?", a: "ราคาขึ้นอยู่กับจำนวนเอกสาร ภาษาปลายทาง ความเร่งด่วน และเส้นทางรับรอง (MFA เท่านั้น vs MFA + Embassy) — กรอกเอกสารใน Quick Quote เพื่อรับใบเสนอราคาที่แน่นอน" },
  { q: "มีบริการ Express ด่วนภายในวันเดียวไหม?", a: "มีสำหรับ Notary, แปลเร่งด่วน, และ MFA Express — Surcharge 50–100% ขึ้นกับคิวและช่วงเวลา ติดต่อทีมงานเพื่อเช็คคิวสด" },
  { q: "จ่ายเงินอย่างไร มีใบเสร็จ/ใบกำกับภาษีหรือไม่?", a: "โอนผ่านธนาคารหรือ PromptPay มีใบเสร็จและใบกำกับภาษีเต็มรูปแบบในนามบริษัททุกครั้ง ลูกค้าองค์กรสามารถขอใบแจ้งหนี้ (Invoice) ก่อนชำระได้" },
  { q: "ราคามีส่วนลดสำหรับงานปริมาณมากหรือลูกค้าองค์กรไหม?", a: "มี — เอกสารตั้งแต่ 10 ฉบับขึ้นไป หรือ Contract ระยะยาวกับองค์กร/HR/Law Firm รับส่วนลด 10–25% ติดต่อทีม B2B" },
  { q: "ถ้ายกเลิกงานหลังจ่ายมัดจำแล้ว ได้เงินคืนไหม?", a: "ค่าธรรมเนียมราชการที่ชำระไปแล้วคืนไม่ได้ ค่าบริการคืน 50% หากยังไม่เริ่มดำเนินการ คืน 0% หากเริ่มแล้ว — รายละเอียดตามใบเสนอราคา" },
];

export const Route = createFileRoute("/pricing-matrix")({
  component: PricingMatrixPage,
  loader: async (): Promise<{
    services: ServiceItem[];
    categories: { id: string; label: string; sub?: string }[];
  }> => {
    const { SERVICES, CATEGORIES } = await import("@/lib/services");
    return { services: SERVICES, categories: CATEGORIES };
  },
  head: ({ loaderData }) => {
    const site = getActiveSiteUrl();
    const pricedItems = (loaderData?.services ?? []).filter((s) => s.priceFrom);
    const priced = pricedItems.length;
    const head = pageHeadForProvince({
      title: `ตารางราคาแบบ Interactive — Filter ${priced}+ บริการ | Thai Notary`,
      description: `ตารางราคารวมทุกบริการ Notary, แปล, MFA, Apostille, Police Clearance, Visa, Marriage — กรองตามหมวด งบประมาณ และคำค้นได้ทันที โปร่งใส ไม่มีค่าซ่อน`,
      path: "/pricing-matrix",
    });
    return {
      ...head,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildOrganizationSchema()) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "ราคา", url: "/pricing" },
              { name: "ตารางราคา Interactive", url: "/pricing-matrix" },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(FAQS)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Interactive Pricing Matrix",
            numberOfItems: priced,
            itemListElement: pricedItems.map((s, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `${site}/services/${s.slug}`,
              name: s.title,
            })),
          }),
        },
        // PriceSpecification offer catalog — gives AI / Google a structured
        // price for every priced service so AI Quick Answer Box can cite "from ฿1,500"
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "OfferCatalog",
            "@id": `${site}/pricing-matrix#catalog`,
            name: "Thai Notary Service Price Catalog",
            itemListElement: pricedItems.map((s) => ({
              "@type": "Offer",
              url: `${site}/services/${s.slug}`,
              name: s.title,
              category: s.category,
              priceCurrency: "THB",
              price: s.priceFrom,
              priceSpecification: {
                "@type": "PriceSpecification",
                priceCurrency: "THB",
                minPrice: s.priceFrom,
                maxPrice: s.priceTo ?? s.priceFrom,
                unitText: s.unit ?? "per document",
                valueAddedTaxIncluded: false,
              },
              availability: "https://schema.org/InStock",
              seller: { "@id": `${site}/#organization` },
              areaServed: { "@type": "Country", name: "Thailand" },
            })),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/pricing-matrix")) },
      ],
    };
  },
});

function fmtBaht(n?: number) {
  if (!n) return "—";
  return n.toLocaleString("th-TH");
}

function PricingMatrixPage() {
  const { services: SERVICES, categories: CATEGORIES } = Route.useLoaderData() as {
    services: ServiceItem[];
    categories: { id: string; label: string; sub?: string }[];
  };
  const [cat, setCat] = useState<ServiceCategory | "all">("all");
  const [budget, setBudget] = useState<string>("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<SortKey>("category");

  const items = useMemo(() => {
    const tier = BUDGET_TIERS.find((t) => t.id === budget)!;
    let out = SERVICES.filter((s) => s.priceFrom);
    if (cat !== "all") out = out.filter((s) => s.category === cat);
    if (budget !== "all") {
      out = out.filter((s) => {
        const lo = s.priceFrom ?? 0;
        return lo >= tier.min && lo < tier.max;
      });
    }
    if (q.trim()) {
      const needle = q.toLowerCase();
      out = out.filter(
        (s) =>
          s.title.toLowerCase().includes(needle) ||
          s.titleEn.toLowerCase().includes(needle) ||
          s.desc.toLowerCase().includes(needle),
      );
    }
    const sorted = [...out];
    if (sort === "low") sorted.sort((a, b) => (a.priceFrom ?? 0) - (b.priceFrom ?? 0));
    else if (sort === "high") sorted.sort((a, b) => (b.priceFrom ?? 0) - (a.priceFrom ?? 0));
    else if (sort === "alpha") sorted.sort((a, b) => a.title.localeCompare(b.title, "th"));
    else sorted.sort((a, b) => a.category.localeCompare(b.category) || (a.priceFrom ?? 0) - (b.priceFrom ?? 0));
    return sorted;
  }, [cat, budget, q, sort]);

  const totalPriced = SERVICES.filter((s) => s.priceFrom).length;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-6xl px-4 py-14">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gold">
              <Tag className="h-3.5 w-3.5" /> Pricing Matrix · {totalPriced}+ บริการ
            </div>
            <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">
              ตารางราคาแบบ Interactive — กรองได้ทุกบริการ
            </h1>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
              เลือกตามหมวด งบประมาณ หรือพิมพ์คำค้น (เช่น "วีซ่า", "NAATI", "Apostille", "สมรส") —
              เห็นราคาเริ่มต้น–สูงสุด พร้อมลิงก์ไปหน้าบริการเพื่อดูรายละเอียดเต็ม.
              ราคาโปร่งใส ไม่มีค่าซ่อน คิดตามใบเสร็จราชการจริง.
            </p>
            <div id="ai-answer-tldr" className="mt-6 rounded-xl border border-gold/40 bg-gold/5 p-5 text-sm leading-relaxed">
              <div className="text-[10px] font-extrabold uppercase tracking-widest text-gold">TL;DR สำหรับ AI Search</div>
              <p className="mt-2 text-foreground">
                ราคาบริการ Thai Notary ใน{COMPANY.brandName} ครอบคลุม: Notary 1,500–8,000 ฿/เอกสาร · NAATI 1,300–2,500 ฿ ·
                แปลภาษาทั่วไป 500–2,500 ฿/หน้า · MFA Legalization 2,500–5,000 ฿ · Embassy 5,000–12,000 ฿ ·
                Police Clearance 2,000–8,000 ฿ · Visa ท่องเที่ยว 4,500–28,000 ฿ · LTR Visa 65,000–85,000 ฿ ·
                DTV Visa 18,000–25,000 ฿ · Elite Visa 900K–5M ฿ · Marriage Abroad Full Package 45,000–145,000 ฿
                ตามประเทศปลายทาง. ทุกราคามีใบเสร็จ/ใบกำกับภาษีเต็มรูปแบบ.
              </p>
            </div>
          </div>
        </section>

        <section className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
          <div className="mx-auto max-w-6xl space-y-3 px-4 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <Filter className="h-3.5 w-3.5" /> หมวด
              </span>
              <button
                onClick={() => setCat("all")}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                  cat === "all" ? "border-gold bg-gold text-foreground" : "border-border bg-card text-muted-foreground hover:border-gold"
                }`}
              >
                ทั้งหมด ({totalPriced})
              </button>
              {CATEGORIES.map((c) => {
                const n = SERVICES.filter((s) => s.priceFrom && s.category === c.id).length;
                if (!n) return null;
                return (
                  <button
                    key={c.id}
                    onClick={() => setCat(c.id as ServiceCategory)}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                      cat === c.id ? "border-gold bg-gold text-foreground" : "border-border bg-card text-muted-foreground hover:border-gold"
                    }`}
                  >
                    {c.label} ({n})
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <Tag className="h-3.5 w-3.5" /> งบ
              </span>
              {BUDGET_TIERS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setBudget(t.id)}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                    budget === t.id ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:border-primary"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="ค้นหา: NAATI, MFA, วีซ่าอเมริกา, สมรสญี่ปุ่น ..."
                  className="h-9 w-full rounded-md border border-border bg-card pl-9 pr-3 text-sm outline-none focus:border-gold"
                />
              </div>
              <label className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <ArrowUpDown className="h-3.5 w-3.5" /> Sort
              </label>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as SortKey)}
                className="h-9 rounded-md border border-border bg-card px-2 text-xs font-semibold"
              >
                <option value="category">หมวด</option>
                <option value="low">ราคาต่ำ → สูง</option>
                <option value="high">ราคาสูง → ต่ำ</option>
                <option value="alpha">ก-ฮ</option>
              </select>
            </div>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-6xl px-4 py-8">
            <div className="mb-4 text-sm text-muted-foreground">
              พบ <span className="font-extrabold text-foreground">{items.length}</span> บริการ
              {items.length === 0 && " — ลองลดเงื่อนไขหรือเปลี่ยนช่วงงบประมาณ"}
            </div>
            <div className="overflow-x-auto rounded-2xl border border-border bg-card">
              <table className="min-w-full text-sm">
                <thead className="border-b border-border bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 text-left font-bold">บริการ</th>
                    <th className="px-4 py-3 text-left font-bold">หมวด</th>
                    <th className="px-4 py-3 text-right font-bold">เริ่มต้น</th>
                    <th className="px-4 py-3 text-right font-bold">สูงสุด</th>
                    <th className="px-4 py-3 text-left font-bold">หน่วย</th>
                    <th className="px-4 py-3 text-right font-bold"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {items.map((s) => {
                    const catLabel = CATEGORIES.find((c) => c.id === s.category)?.label ?? s.category;
                    return (
                      <tr key={s.id} className="transition hover:bg-muted/30">
                        <td className="px-4 py-3">
                          <Link
                            to="/services/$service"
                            params={{ service: s.slug }}
                            className="font-bold text-foreground hover:text-primary"
                          >
                            {s.title}
                          </Link>
                          <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{s.desc}</div>
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">{catLabel}</td>
                        <td className="px-4 py-3 text-right font-extrabold text-primary">
                          {fmtBaht(s.priceFrom)} ฿
                        </td>
                        <td className="px-4 py-3 text-right text-muted-foreground">
                          {s.priceTo ? `${fmtBaht(s.priceTo)} ฿` : "—"}
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">{s.unit ?? "ต่อเอกสาร"}</td>
                        <td className="px-4 py-3 text-right">
                          <Link
                            to="/services/$service"
                            params={{ service: s.slug }}
                            className="inline-flex items-center gap-1 rounded-md border border-gold/50 bg-gold/10 px-2.5 py-1 text-[11px] font-bold text-gold transition hover:bg-gold hover:text-foreground"
                          >
                            ดูบริการ →
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="border-b border-border bg-primary/5">
          <div className="mx-auto max-w-6xl px-4 py-12">
            <h2 className="text-2xl font-extrabold">รวมขั้นตอน Workflow — เห็นต้นทุนจริงในเส้นทางที่นิยม</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {[
                {
                  name: "Marriage Bundle (ต่างชาติ + คนไทย)",
                  items: [
                    "แปลเอกสารต่างชาติ (Single Status, Passport) 1,500–3,000",
                    "MFA Legalization 2,500–5,000",
                    "Embassy ของฝ่ายต่างชาติ 5,000–12,000",
                    "ค่าธรรมเนียมจดทะเบียนอำเภอ 0 (ฟรี)",
                  ],
                  total: "9,000 – 20,000 ฿",
                  cta: "/services/marriage-registration",
                },
                {
                  name: "Visa Bundle (ยื่นต่างประเทศ)",
                  items: [
                    "Police Clearance ใช้ต่างประเทศ 4,000–8,000",
                    "แปล NAATI/MoJ 1,300–2,500",
                    "MFA + Embassy 7,500–17,000",
                    "Visa Consult + ใบสมัคร 4,500–28,000",
                  ],
                  total: "17,300 – 55,500 ฿",
                  cta: "/services/visa-consult-thai",
                },
                {
                  name: "Notary Bundle (สำหรับ Expat)",
                  items: [
                    "Notary Public 1,500–8,000",
                    "MFA Legalization 2,500–5,000",
                    "Embassy ของประเทศปลายทาง 5,000–12,000",
                    "จัดส่งระหว่างประเทศ EMS 2,500",
                  ],
                  total: "11,500 – 27,500 ฿",
                  cta: "/services/notary-public-english",
                },
              ].map((b) => (
                <div key={b.name} className="rounded-2xl border border-border bg-card p-5">
                  <div className="text-sm font-extrabold text-foreground">{b.name}</div>
                  <ul className="mt-3 space-y-1.5 text-xs text-muted-foreground">
                    {b.items.map((i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-gold" /> {i}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 rounded-md border border-gold/40 bg-gold/10 px-3 py-2 text-xs font-extrabold text-gold">
                    รวมประมาณ: {b.total}
                  </div>
                  <a
                    href={b.cta}
                    className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline"
                  >
                    ดูบริการเต็ม →
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-3xl px-4 py-12">
            <h2 className="text-2xl font-extrabold">คำถามที่พบบ่อยเรื่องราคา</h2>
            <div className="mt-6 space-y-3">
              {FAQS.map((f) => (
                <details key={f.q} className="group rounded-xl border border-border bg-card p-4">
                  <summary className="cursor-pointer list-none font-bold text-foreground">
                    <span className="mr-2 text-gold">Q.</span>{f.q}
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border bg-gradient-to-b from-background to-primary/5">
          <div className="mx-auto max-w-4xl px-4 py-14 text-center">
            <h2 className="text-2xl font-extrabold md:text-3xl">รับใบเสนอราคาเฉพาะเคสคุณ ฟรี 24 ชม.</h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              ส่งรายการเอกสารและประเทศปลายทาง — ทีมงานคำนวณค่าบริการ + ค่าธรรมเนียมราชการ + Timeline ให้ในที่เดียว
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <a
                href={`tel:${COMPANY.phoneIntl}`}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90"
              >
                <Phone className="h-4 w-4" /> โทร {COMPANY.phone}
              </a>
              <a
                href={`https://line.me/ti/p/~${COMPANY.lineId.replace(/^@/, "")}`}
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold text-foreground hover:bg-gold/90"
              >
                <MessageCircle className="h-4 w-4" /> LINE {COMPANY.lineId}
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-6 py-3 text-sm font-bold text-foreground hover:border-gold"
              >
                ส่งเอกสารผ่านฟอร์ม
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}